import './guide.less'; // 包含蒙层和高亮区域的样式

import { Button, Popover } from '@bfe/moly';
import { CloseIcon } from '@bfe/moly-icons';
import { debounce } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useI18nContext } from '@/i18n/I18n';

const Guide = ({
  targetSelector,
  containerSelector,
  onClose,
}: {
  targetSelector: string;
  containerSelector: string;
  onClose?: () => void;
}) => {
  const [$t] = useI18nContext();

  const [highlightStyle, setHighlightStyle] = useState({}); // 高亮块样式
  const [containerStyle, setContainerStyle] = useState({}); // 引导组件样式
  const [bodyStyle, setBodyStyle] = useState<any>(null); // body 样式

  // 设置body样式，弹出引导层时，禁止页面滚动
  const setBodyHidden = () => {
    const style = document.createElement('style');
    style.innerHTML = `html, body { height: 100vh; overflow: hidden }`;
    document.head.appendChild(style);
    setBodyStyle(style);
  };

  // 设置引导层容器和高亮块样式
  const setStyle = (container: any, target: any) => {
    const padding = 8; // 高亮块内边距
    if (target && container) {
      const targetRect = target.getBoundingClientRect(); // 获取引导内容的位置和宽高
      const containerRect = container.getBoundingClientRect(); // 获取引导层需要覆盖的元素的位置和宽高
      const containerStyles = window.getComputedStyle(container); // 获取引导层需要覆盖的元素的样式(z-index 和 border-radius)
      if (
        containerRect.width &&
        containerRect &&
        targetRect.width &&
        targetRect.height
      ) {
        setContainerStyle({
          top: containerRect.top,
          left: containerRect.left,
          width: containerRect.width,
          height: containerRect.height,
          borderRadius: containerStyles.borderRadius,
          zIndex: containerStyles.zIndex,
        });

        setHighlightStyle({
          top: targetRect.top - containerRect.top - padding,
          left: targetRect.left - containerRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
        });

        setBodyHidden(); // 禁止页面滚动
      }
    }
  };

  // 关闭引导层时，重置样式
  const clearStyle = () => {
    if (bodyStyle) {
      bodyStyle.remove(); // 删除html 和 body 样式
    }
  };

  // 点击关闭引导层
  const handleClose = () => {
    localStorage.setItem('islamic-guide', '1'); // 存储已关闭引导层状态
    clearStyle(); // 清除引导层设置的样式
    onClose?.(); // 关闭引导层回调
  };

  useEffect(() => {
    console.log('containerSelector', containerSelector, targetSelector);
    if (containerSelector && targetSelector) {
      const container = document.querySelector(containerSelector); // 获取引导层要覆盖的元素
      const target = document.querySelector(targetSelector); // 获取要高亮的内容
      if (container && target) {
        // 监听高亮内容是否在视窗可见
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setStyle(container, target); // 元素可见时，显示引导层
            }
          });
        });

        // 观察目标元素，监听属性变化
        observer.observe(container);

        const debounceFn = debounce(() => {
          setStyle(container, target); // 窗口尺寸改变时，更新引导层样式
        }, 150);

        debounceFn();

        window.addEventListener('resize', debounceFn);

        return () => {
          observer.unobserve(container); // 组件卸载时，取消监听
          window.removeEventListener('resize', debounceFn);
        };
      }
    }
  }, [containerSelector, targetSelector]);

  useEffect(() => {
    return () => {
      clearStyle(); // 组件卸载时，重置样式
    };
  }, []);

  return createPortal(
    <div className="guide-container" style={containerStyle}>
      <Popover
        content={
          <div className="guide-content">
            <div className="guide-header">
              <span>{$t('islamicGuideTitle')}</span>
              <CloseIcon onClick={handleClose} />
            </div>
            <p className="guide-desc">{$t('islamicGuideDesc')}</p>
            <div className="guide-footer">
              <Button onClick={handleClose} variant="primary" size="x-small">
                {$t('islamicGuideBtnText')}
              </Button>
            </div>
          </div>
        }
        visible
      >
        <div className="guide-highlight" style={highlightStyle} />
      </Popover>
    </div>,
    document.body,
  );
};

export default Guide;
